// src/config/planLimits.ts

export interface AIPackage {
  id: string; calls: number; price: number; name: string; description: string; popular?: boolean;
}

export interface PlanLimits {
  maxSubscribers: number;
  maxTemplates: number;
  maxTeamMembers: number;
  maxAIAnalyses: number;
  maxSmartBriefs: number;
  maxEmailTracking: number;
  maxAPICalls: number;
  maxShareLinks: number;
  aiEmailsPerMonth: number;
  hasBehavioralLearning: boolean;
  hasAdvancedAnalytics: boolean;
  hasAPIAccess: boolean;
  hasComplianceTools: boolean;
  monthlyPrice: number;
  yearlyPrice: number;
  displayName: string;
}

export const AI_ANALYSIS_PACKAGES: AIPackage[] = [
  { id: 'ai-basic', calls: 5, price: 9.99, name: '5 Analyses', description: '5 additional AI analyses' },
  { id: 'ai-pro', calls: 15, price: 24.99, name: '15 Analyses', description: '15 additional AI analyses', popular: true },
  { id: 'ai-enterprise', calls: 30, price: 39.99, name: '30 Analyses', description: '30 additional AI analyses' }
];

export const PLAN_LIMITS: Record<'starter' | 'pro' | 'premium', PlanLimits> = {
  starter: {
    maxSubscribers: 1000, maxTemplates: 5, maxTeamMembers: 1,
    maxAIAnalyses: 10, maxSmartBriefs: 10, maxEmailTracking: 5,
    maxAPICalls: 10, maxShareLinks: Infinity, aiEmailsPerMonth: Infinity,
    hasBehavioralLearning: false, hasAdvancedAnalytics: false,
    hasAPIAccess: false, hasComplianceTools: true,
    monthlyPrice: 29, yearlyPrice: 21, displayName: 'Starter'
  },
  pro: {
    maxSubscribers: 10000, maxTemplates: 20, maxTeamMembers: 5,
    maxAIAnalyses: 30, maxSmartBriefs: 30, maxEmailTracking: 200,
    maxAPICalls: 20, maxShareLinks: Infinity, aiEmailsPerMonth: Infinity,
    hasBehavioralLearning: true, hasAdvancedAnalytics: true,
    hasAPIAccess: true, hasComplianceTools: true,
    monthlyPrice: 99, yearlyPrice: 79, displayName: 'Professional'
  },
  premium: {
    maxSubscribers: 50000, maxTemplates: Infinity, maxTeamMembers: Infinity,
    maxAIAnalyses: 50, maxSmartBriefs: 50, maxEmailTracking: Infinity,
    maxAPICalls: 50, maxShareLinks: Infinity, aiEmailsPerMonth: Infinity,
    hasBehavioralLearning: true, hasAdvancedAnalytics: true,
    hasAPIAccess: true, hasComplianceTools: true,
    monthlyPrice: 299, yearlyPrice: 249, displayName: 'Enterprise'
  }
};

const normalizePlanName = (plan: string): 'starter' | 'pro' | 'premium' => {
  const n = plan?.toLowerCase() || 'starter';
  if (n === 'enterprise' || n === 'premium') return 'premium';
  if (n === 'professional' || n === 'pro') return 'pro';
  return 'starter';
};

export const getUserPlanLimits = (plan?: string): PlanLimits => {
  return PLAN_LIMITS[normalizePlanName(plan || 'starter')] || PLAN_LIMITS.starter;
};

export const hasReachedLimit = (current: number, max: number): boolean => {
  if (max === Infinity) return false;
  return current >= max;
};

export const getRemaining = (used: number, limit: number): number => {
  if (limit === Infinity) return Infinity;
  return Math.max(0, limit - used);
};

export const canUseAIAnalysis = (plan: string, used: number): boolean => {
  return !hasReachedLimit(used, getUserPlanLimits(plan).maxAIAnalyses);
};

export const canUseSmartBrief = (plan: string, used: number): boolean => {
  return !hasReachedLimit(used, getUserPlanLimits(plan).maxSmartBriefs);
};

export const getPlanDisplayName = (plan: string): string => {
  return getUserPlanLimits(plan).displayName;
};

// Keep old functions for compatibility
export const formatLimit = (limit: number): string => {
  if (limit === Infinity) return 'Unlimited';
  if (limit >= 1000) return `${(limit / 1000).toFixed(0)}k`;
  return limit.toString();
};

export const getUsagePercentage = (used: number, limit: number): number => {
  if (limit === Infinity || limit === 0) return 0;
  return Math.min(100, (used / limit) * 100);
};

export const getAvailableAIPackages = (plan: string): AIPackage[] => {
  return AI_ANALYSIS_PACKAGES;
};

export const getAIAnalysisLimit = (plan: string): number => {
  return getUserPlanLimits(plan).maxAIAnalyses;
};

export const templateAccess = { basic: 'basic' as const, premium: 'premium' as const, all: 'all' as const };

export const canAccessTemplate = (plan: string, tier: 'basic' | 'premium'): boolean => {
  const limits = getUserPlanLimits(plan);
  if (plan === 'premium' || plan === 'enterprise') return true;
  if (plan === 'pro' && tier === 'basic') return true;
  if (plan === 'starter' && tier === 'basic') return true;
  return false;
};

export const getTemplateAccessLevel = (plan: string): 'basic' | 'premium' | 'all' => {
  if (plan === 'premium' || plan === 'enterprise') return 'all';
  if (plan === 'pro') return 'premium';
  return 'basic';
};

export const getPlanPricing = (plan: string) => {
  const limits = getUserPlanLimits(plan);
  return {
    monthly: limits.monthlyPrice,
    yearly: limits.yearlyPrice,
    currency: 'USD',
    savingsPercent: Math.round((1 - limits.yearlyPrice / limits.monthlyPrice) * 100),
    savingsAmount: (limits.monthlyPrice - limits.yearlyPrice) * 12
  };
};

export const formatPrice = (price: number, period: 'monthly' | 'yearly' = 'monthly'): string => {
  return `$${price}/${period === 'monthly' ? 'mo' : 'yr'}`;
};

export const hasFeature = (plan: string, feature: string): boolean => {
  const limits = getUserPlanLimits(plan);
  const value = (limits as any)[feature];
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0 || value === Infinity;
  return !!value;
};

export const getFeatureFlags = (plan: string) => {
  const limits = getUserPlanLimits(plan);
  return {
    canUseAI: true,
    hasAdvancedAI: limits.hasBehavioralLearning,
    hasBehavioralLearning: limits.hasBehavioralLearning,
    hasComplianceTools: limits.hasComplianceTools,
    hasAdvancedAnalytics: limits.hasAdvancedAnalytics,
    hasAPIAccess: limits.hasAPIAccess,
    templateAccessLevel: getTemplateAccessLevel(plan),
    templateCount: limits.maxTemplates,
    teamMembers: limits.maxTeamMembers,
    freeAIAnalysisPerMonth: limits.maxAIAnalyses,
    canPurchaseAIAnalysis: true
  };
};

export const getUpgradeSuggestion = (plan: string, usage: any) => {
  return { shouldUpgrade: false, reasons: [] };
};

export const comparePlans = (planA: string, planB: string) => {
  const a = getUserPlanLimits(planA);
  const b = getUserPlanLimits(planB);
  return { subscribers: { [planA]: a.maxSubscribers, [planB]: b.maxSubscribers } };
};